// Composing, on the dashboard.
//
// The same composer as the full screen — same upload, same validation, same
// `create_post` — showing the controls that the common post needs and linking
// to the screen for the rest. It is deliberately not a cut-down copy: every
// button here calls into `useComposer`, so a post written on the dashboard and
// a post written on /compose are the same post made the same way.
//
// What it leaves out, and where each goes instead:
//   reordering a carousel   → the full screen numbers and moves the slides
//   YouTube title & privacy → the full screen, which is why picking a YouTube
//                             account here says so and links there
//   templates and tokens    → the caption library

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MediaPicker } from "../components/MediaPicker";
import { DraftPicker } from "../components/DraftPicker";
import { TargetPicker } from "../components/TargetPicker";
import { Spinner } from "../components/Spinner";
import { MAX_PHOTOS } from "./types";
import { useSharedComposer } from "./ComposerContext";

export function QuickComposeCard() {
  const c = useSharedComposer();
  const [picking, setPicking] = useState(false);
  const [pickingDraft, setPickingDraft] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // YouTube wants a title and a visibility choice before it will take a video,
  // and neither belongs on a card this size. Say so, rather than letting the
  // publish button sit disabled with no explanation.
  const needsFullScreen = c.kind === "video" && c.selectedPlatforms.includes("youtube");

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])];
    if (fileInput.current) fileInput.current.value = "";
    await c.addFiles(files);
  }

  return (
    <section className="card quick-compose" aria-labelledby="quick-compose-head">
      <div className="card-head">
        <div>
          <h2 id="quick-compose-head">Quick compose</h2>
          <p className="muted card-sub">Write it here, or open the full composer for everything else.</p>
        </div>
        <div className="quick-head-actions">
          {/* Not gated on the AI plan: a saved draft is a saved draft, however
              it was written. The paywall is on making them, in the studio. */}
          <button type="button" className="sm" onClick={() => setPickingDraft(true)}
                  disabled={c.busy}>
            Use a saved draft
          </button>
          <Link to="/compose" className="quick-open">Full composer →</Link>
        </div>
      </div>

      <label className="visually-hidden" htmlFor="quick-caption">What are you posting?</label>
      <textarea
        id="quick-caption"
        className="quick-caption"
        placeholder="What are you posting?"
        value={c.caption}
        disabled={c.busy}
        onChange={(e) => c.setCaption(e.target.value)}
      />

      <div className="quick-media">
        <input ref={fileInput} type="file" accept="image/*,video/*" multiple onChange={pick}
               className="visually-hidden" tabIndex={-1} />
        <button type="button" className="quick-drop"
                onClick={() => fileInput.current?.click()}
                disabled={!c.mayUpload || c.busy || !c.canAddMore}>
          <strong>Add photos or video</strong>
          <span className="muted">Up to {MAX_PHOTOS} photos, or one video</span>
        </button>

        {c.items.map((it, i) => (
          <span key={it.key} className="quick-thumb">
            {it.previewUrl
              ? it.facts.mimeType.startsWith("video/") && !(it.asset && it.asset.preview_path)
                ? <video src={it.previewUrl} preload="metadata" muted playsInline />
                : <img src={it.previewUrl} alt={`Photo ${i + 1}`} />
              : <span className="thumb-fallback">🖼</span>}
            <button type="button" className="quick-thumb-x" disabled={c.busy}
                    aria-label={`Remove photo ${i + 1}`}
                    onClick={() => c.removeItem(it.key)}>×</button>
          </span>
        ))}

        <button type="button" className="quick-more" onClick={() => setPicking(true)}
                disabled={!c.workspaceId || c.busy || !c.canAddMore}
                title="Choose something already in your media library">
          <span aria-hidden="true">＋</span>
          <span className="muted">From library</span>
        </button>
      </div>

      {c.videoBlocked && (
        <div className="notice upgrade">
          Video publishing is not included in your plan.{" "}
          <Link to="/plans">See plans</Link> — or post a photo instead.
        </div>
      )}

      <div className="quick-targets">
        <h3>Post to</h3>
        {c.facts ? (
          <TargetPicker
            connections={c.activeConnections}
            selected={c.selected}
            results={c.results}
            checking={c.checking}
            onToggle={c.toggleTarget}
            onToggleMany={c.setTargets}
            onFix={c.applyFix}
            brands={c.brands}
            activeBrandId={c.activeBrandId}
          />
        ) : c.activeConnections.length === 0 ? (
          <p className="muted">
            No accounts connected yet. <Link to="/connections">Connect one</Link> and it appears
            here and in the rail on the left.
          </p>
        ) : (
          // Chips before there is media would be chips that cannot be checked:
          // the verdicts are about the file, so there is nothing to say yet.
          <p className="muted">
            Add a photo or video and your connected accounts appear here, each with whether it
            will accept the file.
          </p>
        )}
      </div>

      {scheduling && (
        <div className="quick-when">
          <label htmlFor="quick-when">
            Send at <span className="muted">({c.zoneName})</span>
          </label>
          <input id="quick-when" type="datetime-local" value={c.scheduledAt}
                 disabled={!c.canSchedule} max={c.horizonInputMax}
                 onChange={(e) => c.setScheduledAt(e.target.value)} />
          {!c.canSchedule && (
            <p className="notice upgrade">
              Scheduling is a paid feature. Your posts publish immediately.{" "}
              <Link to="/plans">See plans</Link>
            </p>
          )}
        </div>
      )}

      {needsFullScreen && (
        <div className="notice">
          YouTube needs a title and a visibility choice before a video can go out.{" "}
          <Link to="/compose">Finish this in the full composer</Link>.
        </div>
      )}

      {c.notice && <div className="notice">{c.notice}</div>}
      {c.paywall && (
        <div className="notice upgrade">{c.paywall} <Link to="/plans">See plans</Link></div>
      )}
      {c.error && <p className="err quick-error">{c.error}</p>}

      {c.busy && (
        <p className="muted loading-row" role="status">
          <Spinner />
          {c.stage === "uploading-media" && (c.items.length > 1
            ? `Uploading media — ${Math.min(c.uploaded + 1, c.items.length)} of ${c.items.length}…`
            : "Uploading media…")}
          {c.stage === "creating" && "Creating post…"}
          {c.stage === "uploading-youtube" && `Uploading to YouTube — ${c.progress}%`}
          {c.stage === "dispatching" && "Publishing…"}
        </p>
      )}

      <div className="quick-actions">
        <button type="button" className={c.savingDraft ? "is-busy" : undefined}
                onClick={() => void c.saveAsDraft()}
                disabled={c.busy || c.savingDraft || !c.caption.trim()}>
          {c.savingDraft && <Spinner />}
          Save draft
        </button>

        <button type="button" aria-pressed={scheduling}
                className={scheduling ? "on" : undefined}
                onClick={() => {
                  const next = !scheduling;
                  setScheduling(next);
                  // Turning it off must clear the time, or the button would
                  // still say Schedule with nothing on screen to explain it.
                  if (!next) c.setScheduledAt("");
                }}
                disabled={c.busy}>
          Schedule
        </button>

        <span className="spacer" />

        <button type="button" className={c.busy ? "primary is-busy" : "primary"}
                onClick={() => void c.publish()}
                disabled={c.busy || !c.allReady || c.videoBlocked || c.postsLeft === 0 ||
                          c.blockedByRole || needsFullScreen ||
                          (scheduling && !c.scheduledAt)}>
          {c.busy && <Spinner />}
          {c.scheduledAt ? "Schedule post" : "Publish now"}
        </button>
      </div>

      {/* One line under the buttons, and only when there is a reason the
          primary one cannot be pressed. A disabled button with no explanation
          is the thing this whole screen is meant to stop. */}
      {!c.busy && (
        c.blockedByRole ? (
          <p className="muted quick-why">
            Your role can prepare posts but not {c.scheduledAt ? "schedule" : "publish"} them — ask
            a manager or publisher in your workspace.
          </p>
        ) : c.postsLeft === 0 ? (
          <p className="muted quick-why">
            You have used every post in your plan this month. <Link to="/plans">See plans</Link>
          </p>
        ) : c.facts && c.selected.size > 0 && !c.allReady && !c.checking ? (
          <p className="muted quick-why">Resolve the notes above before publishing.</p>
        ) : c.postsLeft !== null && c.postsLeft > 0 && c.postsLeft <= 3 ? (
          <p className="muted quick-why">{c.postsLeft} posts left this month on your plan.</p>
        ) : null
      )}

      {pickingDraft && c.workspaceId && (
        <DraftPicker
          workspaceId={c.workspaceId}
          kind="caption"
          onPick={(d) => { c.replaceCaption(d.body, "draft"); setPickingDraft(false); }}
          onClose={() => setPickingDraft(false)}
        />
      )}

      {picking && c.workspaceId && (
        <MediaPicker
          workspaceId={c.workspaceId}
          kind={c.selected.size > 0 ? c.kind : null}
          max={Math.max(c.slotsLeft, 1)}
          onPick={(assets, urls) => { setPicking(false); c.useAssets(assets, urls); }}
          onClose={() => setPicking(false)}
        />
      )}
    </section>
  );
}
